// Content Detail View - displays detailed view of submitted/approved/rejected content

import type { ContentRequirement } from "@/lib/queries/content";
import {
  ContentState,
  getContentDisplayName,
  getPostTypeDisplayName,
} from "@/lib/queries/content";
import {
  Button,
  Card,
  CardContent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@repo/ui";
import { ArrowLeft, ExternalLink, Send, Trash2, Upload } from "lucide-react";
import {
  ApprovedAlert,
  AwaitingPostAlert,
  BrandCommentAlert,
  ChangesRequestedAlert,
  PendingApprovalAlert,
} from "./content-status-alert";
import {
  ApprovedBadge,
  ChangesRequestedBadge,
  CompletedBadge,
  PendingApprovalBadge,
} from "./content-status-badge";
import { UsageRightsDetailsCard } from "./usage-rights-details-card";
import { ContentPreview, extractContentMedia } from "./content-preview";
import { SocialPostEmbed, parseEmbedUrl } from "./social-post-embed";

// ============================================================================
// Types
// ============================================================================

type ContentDetailStatus = "pending" | "approved" | "rejected" | "completed";

interface ContentDetailViewProps {
  /** The content requirement to display */
  requirement: ContentRequirement;
  /** Callback when back button is clicked */
  onBack: () => void;
  /** Callback when edit/resubmit is clicked */
  onEdit?: () => void;
  /** Brand comment on the submission (fetched separately in some cases) */
  brandComment?: string | null;
  /** Custom duration value from agreement (e.g. 2 for "2 months") */
  usageRightsDurationCustomValue?: number | null;
  /** Custom duration unit from agreement ("days", "months", "years") */
  usageRightsDurationCustomUnit?: string | null;
  /**
   * Whether the brief grants content usage rights. Used so the usage-rights
   * card does not report "not granted" for briefs that record rights as
   * free-text legal language without a structured duration (EPD-9453).
   */
  requireContentRights?: boolean;
  /** Called when the creator clicks the delete YouTube video collab button */
  onDeleteYouTubeVideoCollab?: (videoUrl: string) => void;
  /** Whether the delete YouTube collab mutation is in progress */
  isDeletingYouTubeCollab?: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

function getContentStatus(
  requirement: ContentRequirement,
): ContentDetailStatus {
  const { state, dateApproved, completedContentCount } = requirement;

  // Check for rejected state
  if (state === ContentState.REJECTED || state === ContentState.SOFT_REJECTED) {
    return "rejected";
  }

  // Check for completed states
  if (
    state === ContentState.COMPLETED ||
    state === ContentState.MARKED_COMPLETE ||
    state === ContentState.COMPLETED_WITHOUT_PAYMENT ||
    completedContentCount > 0
  ) {
    return "completed";
  }

  // Check for approved states (approved but not yet completed/posted)
  if (
    state === ContentState.ACCEPTED ||
    state === ContentState.SOFT_APPROVED ||
    dateApproved
  ) {
    return "approved";
  }

  // Check for pending states
  if (
    state === ContentState.UPLOADED ||
    state === ContentState.VALIDATING_UPLOAD ||
    state === ContentState.AMENDED ||
    state === ContentState.GCR_PENDING ||
    state === ContentState.PENDING_MARK_COMPLETE
  ) {
    return "pending";
  }

  // Default to pending for NEW or other states
  return "pending";
}

export function getRejectedGuidelines(
  requirement: ContentRequirement,
): string[] {
  const checkoffGuidelines = requirement.deliverable?.checkoffGuidelines;
  if (!checkoffGuidelines) return [];

  const rejected: string[] = [];

  for (const group of checkoffGuidelines) {
    const instructions = group.instructions ?? [];
    for (const instruction of instructions) {
      // brandGuidelineCheckoff being true means it was flagged as an issue
      if (instruction.brandGuidelineCheckoff && instruction.text) {
        rejected.push(`${group.type}: ${instruction.text}`);
      }
    }
  }

  return rejected;
}

// ============================================================================
// Component
// ============================================================================

const YOUTUBE_POST_TYPES = new Set([
  "YOUTUBE_DEDICATED",
  "YOUTUBE_MENTION",
  "YOUTUBE_SHORT",
]);

export function ContentDetailView({
  requirement,
  onBack,
  onEdit,
  brandComment,
  usageRightsDurationCustomValue,
  usageRightsDurationCustomUnit,
  requireContentRights,
  onDeleteYouTubeVideoCollab,
  isDeletingYouTubeCollab = false,
}: ContentDetailViewProps) {
  const displayName = getContentDisplayName(requirement);
  const postType = requirement.deliverable?.postType ?? null;
  const postTypeDisplay = getPostTypeDisplayName(postType);
  const requiresApproval = requirement.deliverable?.requiresApproval ?? false;

  // Determine content status
  const status = getContentStatus(requirement);

  // Get dates
  const dateUploaded = requirement.dateFirstUpload;
  const dateApproved = requirement.dateApproved;
  const completedDate = requirement.completedDateTimeStamp;

  // Get caption text (from content object or post object)
  const captionText =
    requirement.content?.caption ?? requirement.post?.text ?? null;

  // Get post link (from content object or post object)
  const postLink = requirement.content?.link ?? requirement.post?.link ?? null;

  // Get media items
  const mediaItems = extractContentMedia(requirement);

  // Get rejected guidelines
  const rejectedGuidelines = getRejectedGuidelines(requirement);

  // Get usage rights info
  const usageRightsDuration = requirement.usageRightsDuration;

  // Status badge to display
  const statusBadge = {
    pending: <PendingApprovalBadge />,
    approved: requiresApproval ? <ApprovedBadge /> : <CompletedBadge />,
    rejected: <ChangesRequestedBadge />,
    completed: <CompletedBadge />,
  }[status];

  // Status alert to display
  const statusAlert = {
    pending: <PendingApprovalAlert dateUploaded={dateUploaded} />,
    approved:
      status === "approved" && requiresApproval ? (
        <AwaitingPostAlert
          contentSubmissionDate={dateUploaded}
          contentApprovalDate={dateApproved}
        />
      ) : (
        <ApprovedAlert
          submittedDate={dateUploaded}
          approvedDate={dateApproved}
          completedDate={completedDate}
        />
      ),
    rejected: (
      <ChangesRequestedAlert
        brandComment={requirement.brandRejectionComment}
        rejectedGuidelines={rejectedGuidelines}
      />
    ),
    completed: (
      <ApprovedAlert
        submittedDate={dateUploaded}
        approvedDate={dateApproved}
        completedDate={completedDate}
      />
    ),
  }[status];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-semibold">{displayName}</h2>
            {statusBadge}
          </div>
          {postType !== "FLEXIBLE" && (
            <p className="text-sm text-muted-foreground mt-1">
              {postTypeDisplay}
            </p>
          )}
        </div>
      </div>

      {/* Usage Rights Details card */}
      {(requirement.dateFirstUpload || requirement.dateApproved) && (
        <UsageRightsDetailsCard
          usageRightsDuration={usageRightsDuration}
          dateApproved={dateApproved}
          dateFirstUpload={dateUploaded}
          requiresApproval={requiresApproval}
          customValue={usageRightsDurationCustomValue}
          customUnit={usageRightsDurationCustomUnit}
          requireContentRights={requireContentRights}
        />
      )}

      {/* Main content card */}
      <Card>
        <CardContent className="pt-6 space-y-6">
          {/* Status alert */}
          {statusAlert}

          {/* Brand comment (for approved content) */}
          {brandComment && status === "completed" && (
            <BrandCommentAlert comment={brandComment} />
          )}

          {/* Post link or social embed (for completed content) */}
          {status === "completed" && postLink && (
            <div>
              {parseEmbedUrl(postLink) ? (
                <SocialPostEmbed postUrl={postLink} />
              ) : (
                <div className="flex items-center gap-2">
                  <a
                    href={postLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline break-all"
                  >
                    {postLink}
                    <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                  {postType &&
                    YOUTUBE_POST_TYPES.has(postType) &&
                    onDeleteYouTubeVideoCollab && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                              onClick={() =>
                                onDeleteYouTubeVideoCollab(postLink)
                              }
                              disabled={isDeletingYouTubeCollab}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Delete YouTube Video Collab</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                </div>
              )}
            </div>
          )}

          {/* Original Submission */}
          {(mediaItems.length > 0 || captionText) && (
            <div>
              <div className="flex items-center gap-2 text-base font-medium mb-4">
                <Send className="h-4 w-4" />
                Original Submission
              </div>
              <ContentPreview media={mediaItems} caption={captionText} />
            </div>
          )}

          {/* Action buttons based on status */}
          {status === "rejected" && onEdit && (
            <div className="pt-2">
              <Button onClick={onEdit}>
                <Upload className="h-4 w-4 mr-2" />
                Submit Revised Content
              </Button>
            </div>
          )}

          {status === "approved" && requiresApproval && onEdit && (
            <div className="pt-2">
              <p className="text-sm text-muted-foreground mb-3">
                Post the{" "}
                {postType === "FLEXIBLE"
                  ? "content"
                  : postTypeDisplay.toLowerCase()}{" "}
                then submit via Aspire and you're done.
              </p>
              <Button onClick={onEdit}>
                <Upload className="h-4 w-4 mr-2" />
                Submit Post Link
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
